/**
 * RMA status vocabulary shared across the Returns surfaces.
 *
 * One source of truth for the numeric RMA status codes, their display
 * labels, the filter dropdown options, and the status color family — so the
 * list grid and the detail summary render status identically.
 *
 * Labels resolve through i18next at render time via getStatusLabel /
 * buildStatusOptions, reusing the same rma.status* keys the inline status
 * picker already translates.
 *
 * Contains:
 * - RMA_STATUS_KEYS    — numeric code -> i18next key
 * - getStatusLabel     — (t, code) -> translated label
 * - buildStatusOptions — (t) -> [{ value, label }] for filter/select controls
 * - rmaStatusFamily    — numeric code -> StatusChip status family
 */

// Numeric status code -> the rma.status* key shared with StatusChangeControl.
export const RMA_STATUS_KEYS = {
  0: 'rma.statusIssued',
  2: 'rma.statusQuickShipped',
  1: 'rma.statusProcessed',
  100: 'rma.statusCompleted',
  9999: 'rma.statusCanceled',
};

// Order the filter/select options follow.
const RMA_STATUS_ORDER = [0, 2, 1, 100, 9999];

const STATUS_FALLBACKS = {
  0: 'Issued',
  2: 'Quick Shipped',
  1: 'Processed & Shipped',
  100: 'Completed',
  9999: 'Canceled',
};

/**
 * Resolve the translated label for a numeric RMA status.
 * @param {Function} t - i18next translate
 * @param {number} status
 * @returns {string}
 */
export function getStatusLabel(t, status) {
  const key = RMA_STATUS_KEYS[status];
  if (!key) return '';
  return t(key, STATUS_FALLBACKS[status]);
}

/**
 * Build the translated [{ value, label }] options for filter/select controls.
 * @param {Function} t - i18next translate
 * @returns {Array<{ value: string, label: string }>}
 */
export function buildStatusOptions(t) {
  return RMA_STATUS_ORDER.map(code => ({ value: String(code), label: getStatusLabel(t, code) }));
}

// StatusChip status family (cssTokens.js --status-*) per numeric code:
// open states info/warning, shipped/complete ok, cancellation muted.
const RMA_STATUS_FAMILY = {
  0: 'info',
  2: 'warning',
  1: 'ok',
  100: 'ok',
  9999: 'neutral',
};

/**
 * Resolve the status color family for a numeric RMA status.
 * @param {number} status
 * @returns {string}
 */
export function rmaStatusFamily(status) {
  return RMA_STATUS_FAMILY[status] ?? 'neutral';
}
